import { ChangesDetection } from "../../../../change-detection";
import { I_TreatmentResponse } from "../../treatment/api/treatment.response";
import { I_ObservationResponse } from "../../observation/api/observation.response";
import { I_PatientResponse } from "../api/patient.response";
import { I_PatientLocationResponse } from "../api/patient-location.response";
import { I_PatientRequest } from "../api/patient.request";
import { I_PersonResponse } from "../api/person.response";
import { C_Person } from "./person.model";
import { Observable, startWith, map, switchMap, of, shareReplay, filter } from "rxjs";
import { I_GroupedObservationResponse } from "../../grouped-obs/api/grouped-observation.response";
import { E_ChangeAction } from "../../../../../enums/change-action.enum";
import { I_OptionItemResponse } from "../../../../unit/items/master-lists/api/option-list.response";
import { C_OptionItem } from "../../../../unit/items/master-lists/domain/option-list.model";
// import { Observable } from "rxjs";

export class C_PatientDataList extends ChangesDetection<T_PatientDataListChange> {
    private list: Map<string, C_PatientData> = new Map();
    private cachedSubs = new Map<string, Observable<C_PatientData | undefined>>();
    constructor() {
        super();
    }

    getValues(): C_PatientData[] {
        return Array.from(this.list.values());
    }

    has(id: string): boolean {
        return this.list.has(id);
    }

    get(id: string): C_PatientData | undefined {
        return this.list.get(id);
    }

    setPatient(patient?: I_PatientResponse) {
        if (!patient) return;
        if (this.list.has(patient.id)) {
            this.list.get(patient.id)?.updatePatient(patient);
            this.forceUpdateDetection({
                action: E_ChangeAction.Update,
                id: patient.id,
                data: patient,
            });
        } else {
            this.list.set(patient.id, new C_PatientData(patient));
            this.forceUpdateDetection({
                action: E_ChangeAction.Add,
                id: patient.id,
                data: patient,
            });
        }
    }

    deletePatient(patId: string) {
        if (this.list.delete(patId)) {
            this.forceUpdateDetection({ action: E_ChangeAction.Delete, id: patId });
        }
    }
    setTreatmentsBulk(treatList: (I_TreatmentResponse & { localId: symbol })[]) {
        const affectedIds = new Set<string>();
        treatList.forEach(treat => {
            const pat = this.list.get(treat.patientId);
            const treatCode = treat.requestedGiveCodes[0]?.identifier;
            if (pat && treatCode) {
                pat.treatmentsList.add(treatCode);
                affectedIds.add(pat.id!);
            }
        });

        // Disparamos deteccion interna en cada modelo mutado
        affectedIds.forEach(id => this.list.get(id)?.forceUpdateDetection({
            action: E_PatientDataChangeAction.TreatmentsUpdate,
            codes: treatList.filter(t => t.patientId === id).map(t => t.requestedGiveCodes[0].identifier ?? ''),
        }));

        // CORRECCIÓN: Notificamos al List del cambio masivo estructurado
        this.forceUpdateDetection({
            action: E_ChangeAction.AddBulk,
            affectedIds,
            type: E_PatientDataChangeAction.TreatmentsUpdate
        });
    }

    setObservationBulk(obsList: (I_ObservationResponse & { localId: symbol })[]) {
        const affectedIds = new Set<string>();
        obsList.forEach(obs => {
            const pat = this.list.get(obs.patientId);
            const obsKey = obs.name;
            if (pat && obsKey) {
                pat.observations.add(obsKey);
                affectedIds.add(pat.id!);
            }
        });

        affectedIds.forEach(id => this.list.get(id)?.forceUpdateDetection({
            action: E_PatientDataChangeAction.ObservationsUpdate,
            ids: obsList.filter(o => o.patientId === id).map(o => o.name ?? ''),
        }));

        this.forceUpdateDetection({
            action: E_ChangeAction.AddBulk,
            affectedIds,
            type: E_PatientDataChangeAction.ObservationsUpdate
        });
    }

    setGroupedObservationBulk(gObsList: (I_GroupedObservationResponse & { localId: symbol })[]) {
        const affectedIds = new Set<string>();
        gObsList.forEach(obs => {
            if (!obs.patientId) return;
            const pat = this.list.get(obs.patientId);
            const obsKey = obs.name;
            if (pat && obsKey) {
                pat.groupedObservations.add(obsKey);
                affectedIds.add(pat.id!);
            }
        });

        affectedIds.forEach(id => this.list.get(id)?.forceUpdateDetection({
            action: E_PatientDataChangeAction.GroupedObservationsUpdate,
            groups: gObsList.filter(o => o.patientId === id).map(o => o.name ?? ''),
        }));

        this.forceUpdateDetection({
            action: E_ChangeAction.AddBulk,
            affectedIds,
            type: E_PatientDataChangeAction.GroupedObservationsUpdate
        });
    }

    setPerson(data: { id: string, person: C_Person }) {
        if (!data || !this.list.has(data.id)) return;
        this.list.get(data.id)!.updatePerson(data.person.toInterface());

        this.forceUpdateDetection({
            action: E_ChangeAction.Update,
            id: data.id,
            data: this.list.get(data.id)!.toPatientResponse()
        });
    }

    getLivePatient(id: string): Observable<C_PatientData | undefined> {
        if (!id) return of(undefined);
        if (this.cachedSubs.has(id)) return this.cachedSubs.get(id)!;

        const stream$ = this.getForcedChanges().pipe(
            startWith(null),
            filter((change) => {
                if (!change) return true;
                if (change.action === E_ChangeAction.Reset) return true;
                if (change.action === E_ChangeAction.AddBulk) return change.affectedIds.has(id);
                return change.id === id;
            }),
            map(() => this.list.get(id)),
            switchMap(pat => {
                if (!pat) return of(undefined);
                return pat.getForcedChanges().pipe(
                    startWith(pat),
                    map(() => pat)
                );
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.cachedSubs.set(id, stream$);
        return stream$;
    }

    clearSubs() {
        this.cachedSubs.clear();
        this.forceUpdateDetection({ action: E_ChangeAction.Reset });
    }

}

export type T_PatientDataListChange =
    | { action: E_ChangeAction.Add; id: string; data: I_PatientResponse }
    | { action: E_ChangeAction.Update; id: string; data: I_PatientResponse }
    | { action: E_ChangeAction.Delete; id: string }
    | { action: E_ChangeAction.AddBulk; affectedIds: Set<string>; type: E_PatientDataChangeAction }
    | { action: E_ChangeAction.Reset };

export class C_PatientData extends ChangesDetection<T_PatientDataChange> {

    id?: string;
    bed?: string;
    accessStatus?: C_OptionItem;
    patientNumber?: string;
    patientId?: string;
    dischargeStatus?: C_OptionItem;
    origin?: C_OptionItem;
    originAux?: string;
    diagnosisAux?: string;
    diagnosis?: C_OptionItem;
    languageBarrier?: C_OptionItem[];
    epis?: C_OptionItem[];
    hobbies?: C_OptionItem[];
    allergies?: C_OptionItem[];
    admTime?: Date;
    insulation?: C_OptionItem; //Aislamiento: por defecto No Aislado
    passiveSitting?: C_OptionItem;
    alarmStatus?: C_OptionItem;
    nurseCall?: C_OptionItem;
    visits?: C_OptionItem;
    patientStatus?: C_OptionItem; //NEMS
    mobility?: C_OptionItem; //Movilidad: por defecto sin asignar
    therapeuticCeiling?: C_OptionItem; //PruePointOfCarebas e intervencionesfLastObservationDate
    procedures?: C_OptionItem[]; // Intervenciones
    catheters?: C_OptionItem[]; // Sondas
    clinicalRoutes?: C_OptionItem[]; // Vías
    tests?: C_OptionItem[]; // Pruebas
    treatments?: C_OptionItem[]; // Terapias
    disTime?: Date;
    person: C_Person = new C_Person();
    attendingDoctor?: I_PersonResponse; //Facultativos
    doctors?: C_OptionItem[];
    lastObservationDate?: Date;
    creationDate?: Date;
    updateDate?: Date;
    archiveDate?: Date;
    room?: string;
    unitString?: string;
    location?: I_PatientLocationResponse;
    altable?: C_OptionItem;
    observations: Set<string> = new Set(); // IDs
    groupedObservations: Set<string> = new Set(); // GROUPs
    treatmentsList: Set<string> = new Set();// CODEs

    constructor(json?: I_PatientResponse) {
        super();
        if (!json) return;
        this.initializeFromJson(json);
    }

    updatePatient(message: I_PatientResponse) {
        this.initializeFromJson(message);
    }

    updatePerson(message: I_PersonResponse) {
        this.person?.initialize(message);

        this.forceUpdateDetection({
            action: E_PatientDataChangeAction.GeneralUpdate,
            data: this.toPatientResponse() // Emitimos el estado completo actualizado
        });
    }

    getTreatmentsCodes(): string[] {
        return Array.from(this.treatmentsList);
    }

    hasTreatment(treatCode: string): boolean {
        return this.treatmentsList.has(treatCode);
    }

    hasObservation(obsId: string): boolean {
        return this.observations.has(obsId);
    }

    hasGroupedObservation(group: string): boolean {
        return this.groupedObservations.has(group);
    }

    initializeFromJson(json: I_PatientResponse) {
        this.id = json.id;
        this.bed = json.bed;
        this.patientNumber = json.patientNumber;
        this.patientId = json.patientId;
        this.accessStatus = json.accessStatus ? new C_OptionItem(json.accessStatus) : undefined;
        this.alarmStatus = json.alarmStatus ? new C_OptionItem(json.alarmStatus) : undefined;
        this.nurseCall = json.nurseCall ? new C_OptionItem(json.nurseCall) : undefined;
        this.dischargeStatus = json.dischargeStatus ? new C_OptionItem(json.dischargeStatus) : undefined;
        this.origin = json.origin ? new C_OptionItem(json.origin) : undefined;
        this.diagnosis = json.diagnosis ? new C_OptionItem(json.diagnosis) : undefined;
        this.admTime = json.admTime ? new Date(json.admTime) : undefined;
        this.insulation = json.insulation ? new C_OptionItem(json.insulation) : undefined;
        this.passiveSitting = json.passiveSitting ? new C_OptionItem(json.passiveSitting) : undefined;
        this.visits = json.visits ? new C_OptionItem(json.visits) : undefined;
        this.patientStatus = json.patientStatus ? new C_OptionItem(json.patientStatus) : undefined;
        this.mobility = json.mobility ? new C_OptionItem(json.mobility) : undefined;
        this.therapeuticCeiling = json.therapeuticCeiling ? new C_OptionItem(json.therapeuticCeiling) : undefined;
        this.procedures = json.procedures?.length ? json.procedures?.map((p) => new C_OptionItem(p)) : [];
        this.tests = json.tests?.length ? json.tests?.map((t) => new C_OptionItem(t)) : [];
        this.treatments = json.treatment?.length ? json.treatment?.map((t) => new C_OptionItem(t)) : [];
        this.allergies = json.allergies?.length ? json.allergies?.map((a) => new C_OptionItem(a)) : [];
        this.catheters = json.catheters?.length ? json.catheters?.map((c) => new C_OptionItem(c)) : [];
        this.clinicalRoutes = json.clinicalRoutes?.length ? json.clinicalRoutes?.map((c) => new C_OptionItem(c)) : [];
        this.disTime = json.disTime ? new Date(json.disTime) : undefined;
        this.person.initialize(json.person);
        this.attendingDoctor = json.attendingDoctor;
        this.doctors = json.doctors?.length ? json.doctors.map(d => new C_OptionItem(d)) : [];
        this.lastObservationDate = json.lastObservationDate ? new Date(json.lastObservationDate) : undefined;
        this.creationDate = json.creationDate ? new Date(json.creationDate) : undefined;
        this.updateDate = json.updateDate ? new Date(json.updateDate) : undefined;
        this.archiveDate = json.archiveDate ? new Date(json.archiveDate) : undefined;
        this.room = json.room;
        this.unitString = json.unitString;
        this.location = json.location;
        this.altable = json.altable ? new C_OptionItem(json.altable) : undefined;
        json.altable ? new C_OptionItem(json.altable) : this.altable = new C_OptionItem({ id: '', iconDefault: "icNotAltable", name: "NotAltable", optionType: "NotAltable" });
        this.originAux = json.originAux;
        this.diagnosisAux = json.diagnosisAux;
        if (json.languageBarrier != null) {
          json.languageBarrier != null && json.languageBarrier.length>0 ? this.languageBarrier = json.languageBarrier.map(x => new C_OptionItem(x)) : this.languageBarrier = [];
        } else {
            this.languageBarrier = json.languageBarrier;
        }

        try {
            let epi: I_OptionItemResponse[] | undefined = json.epis;
            if (!Array.isArray(json.epis) && json.epis) epi = [json.epis];
            this.epis = epi?.map(a => new C_OptionItem(a)) ?? [];
        } catch (error) {
            console.log("error setteando epis", json.epis);
        }

        try {
            let hobbies: I_OptionItemResponse[] | undefined = json.hobbies;
            if (!Array.isArray(json.hobbies) && json.hobbies) hobbies = [json.hobbies];
            this.hobbies = hobbies?.map(a => new C_OptionItem(a)) ?? [];
        } catch (error) {
            console.log("error setteando hobbies", json.hobbies);
        }

        this.forceUpdateDetection({
            action: E_PatientDataChangeAction.GeneralUpdate,
            data: json,
        });
    }

    toPatientResponse(): I_PatientResponse {
        return {
            id: this.id ?? '',
            accessStatus: this.accessStatus?.toInterface(),
            alarmStatus: this.alarmStatus?.toInterface(),
            nurseCall: this.nurseCall?.toInterface(),
            admTime: this.admTime?.toISOString(),
            allergies: this.allergies?.map(a => a.toInterface()) ?? [],
            altable: this.altable?.toInterface(),
            archiveDate: this.archiveDate?.toISOString(),
            bed: this.bed ?? '',
            diagnosis: this.diagnosis?.toInterface(),
            dischargeStatus: this.dischargeStatus?.toInterface(),
            languageBarrier: this.languageBarrier?.map(x => x.toInterface()),
            origin: this.origin?.toInterface(),
            patientId: this.patientId ?? '',
            patientNumber: this.patientNumber ?? '',
            room: this.room ?? '',
            treatment: this.treatments?.map(t => t.toInterface()),
            unitString: this.unitString ?? '',
            attendingDoctor: this.attendingDoctor,
            creationDate: this.creationDate?.toISOString(),
            diagnosisAux: this.diagnosisAux,
            disTime: this.disTime?.toISOString(),
            doctors: this.doctors?.map(d => d.toInterface()),
            insulation: this.insulation?.toInterface(),
            lastObservationDate: this.lastObservationDate?.toISOString(),
            location: this.location,
            mobility: this.mobility?.toInterface(),
            originAux: this.originAux,
            passiveSitting: this.passiveSitting?.toInterface(),
            patientStatus: this.patientStatus?.toInterface(),
            person: this.person?.toInterface(),
            procedures: this.procedures?.map(p => p.toInterface()),
            tests: this.tests?.map(t => t.toInterface()),
            catheters: this.catheters?.map(t => t.toInterface()),
            clinicalRoutes: this.clinicalRoutes?.map(t => t.toInterface()),
            hobbies: this.hobbies?.map(t => t.toInterface()),
            epis: this.epis?.map(t => t.toInterface()),
            therapeuticCeiling: this.therapeuticCeiling?.toInterface(),
            updateDate: this.updateDate?.toISOString(),
            visits: this.visits?.toInterface(),
        }
    }

    toPatientData(): I_PatientRequest {
        const newPatientData: I_PatientRequest = {
            id: this.id,
            bed: this.bed,
            patientNumber: this.patientNumber,
            patientId: this.patientId,
            dischargeStatus: this.dischargeStatus,
            origin: this.origin,
            originAux: this.originAux,
            diagnosisAux: this.diagnosisAux,
            diagnosis: this.diagnosis,
            languageBarrier: this.languageBarrier,
            allergies: this.allergies,
            admTime: this.admTime ? this.admTime?.toISOString() : undefined,
            insulation: this.insulation,
            passiveSitting: this.passiveSitting,
            visits: this.visits,
            patientStatus: this.patientStatus,
            mobility: this.mobility,
            therapeuticCeiling: this.therapeuticCeiling,
            procedures: this.procedures,
            tests: this.tests,
            catheters: this.catheters,
            clinicalRoutes: this.clinicalRoutes,
            treatment: this.treatments,
            disTime: this.disTime ? this.admTime?.toISOString() : undefined,
            person: this.person?.toInterface(),
            attendingDoctor: this.attendingDoctor,
            doctors: this.doctors,
            lastObservationDate: this.lastObservationDate ? this.admTime?.toISOString() : undefined,
            creationDate: this.creationDate ? this.admTime?.toISOString() : undefined,
            updateDate: this.updateDate ? this.admTime?.toISOString() : undefined,
            archiveDate: this.archiveDate ? this.admTime?.toISOString() : undefined,
            room: this.room,
            unitString: this.unitString,
            location: this.location,
            altable: this.altable,
            epis: this.epis,
            hobbies: this.hobbies,
        };
        return newPatientData;
    }

}

export type T_PatientDataChange =
    | { action: E_PatientDataChangeAction.GeneralUpdate; data: I_PatientResponse }
    | { action: E_PatientDataChangeAction.ObservationsUpdate; ids: string[] }
    | { action: E_PatientDataChangeAction.GroupedObservationsUpdate; groups: string[] }
    | { action: E_PatientDataChangeAction.TreatmentsUpdate; codes: string[] };

export enum E_PatientDataChangeAction {
    GeneralUpdate = 'GENERAL_UPDATE',
    ObservationsUpdate = 'OBSERVATIONS_UPDATE',
    GroupedObservationsUpdate = 'GROUPED_OBSERVATIONS_UPDATE',
    TreatmentsUpdate = 'TREATMENTS_UPDATE',
}
